import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IUser } from "@interfaces/Interfaces";
import { useRecoilState } from "recoil";
import { userAtom } from "@state/State";
import * as DatabaseService from "@controllers/DatabaseService";
import AuthenticationController from "@controllers/AuthenticationController";
import { useLoggedIn } from "@hooks/auth/useLoggedIn";
import { USER_DATA_REFRESH_INTERVAL } from "../../Constants";

type RefreshUserDetails = {
  refreshUserDetails: (_user: IUser) => Promise<void>;
};

const fetchUserDetails = async (user: IUser): Promise<IUser> => {
  if (!user || !user.userData) {
    throw new Error("User is required");
  }

  const userDetails = await DatabaseService.getUserDetails(user);

  if (!userDetails || userDetails.contactID !== `${user.userData.contactID}`) {
    throw new Error("User details not found or contactID mismatch");
  }

  let userToSave: IUser = {
    ...user,
    certificates: userDetails.certificates ? JSON.parse(userDetails.certificates) : undefined,
    usageData: userDetails.dataSharing === 1,
    role: userDetails.role,
    ...(userDetails.personalStatement && {
      personalStatement: userDetails.personalStatement,
    }),
    ...(userDetails.summaryOfWork && {
      summaryOfWork: userDetails.summaryOfWork,
    }),
    ...(userDetails.personalStatementTimestamp && {
      personalStatementTimestamp: userDetails.personalStatementTimestamp,
    }),
    ...(userDetails.summaryOfWorkTimestamp && {
      summaryOfWorkTimestamp: userDetails.summaryOfWorkTimestamp,
    }),
    ...(userDetails.HCPCNumber && {
      HCPCNumber: userDetails.HCPCNumber,
    }),
    programCertificates: userDetails.programCertificates ? JSON.parse(userDetails.programCertificates) : undefined,
    programRoles: userDetails.programRoles ? JSON.parse(userDetails.programRoles) : undefined,
  };

  userToSave = await AuthenticationController.getUserData(userToSave);

  return userToSave;
};

export const useRefreshUserDetails = (shouldLoad?: boolean): RefreshUserDetails => {
  const [user, setUser] = useRecoilState(userAtom);
  const loggedIn = useLoggedIn();
  const queryClient = useQueryClient();

  const isEnabled = Boolean(loggedIn && user && user.userData && shouldLoad);

  const { data: refreshedUser } = useQuery({
    queryKey: ["user-details", user?.userData?.contactID],
    queryFn: () => fetchUserDetails(user!),
    enabled: isEnabled,
    refetchInterval: isEnabled ? USER_DATA_REFRESH_INTERVAL : false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!user || !user.userData) {
      queryClient.removeQueries({ queryKey: ["user-details"] });
    }
  }, [user, queryClient]);

  useEffect(() => {
    if (refreshedUser && refreshedUser.userData?.contactID === user?.userData?.contactID) {
      setUser(refreshedUser);
    }
  }, [refreshedUser, user?.userData?.contactID, setUser]);

  const refreshUserDetails = useCallback(
    async (_user: IUser): Promise<void> => {
      try {
        if (!_user || !_user.userData) {
          return;
        }

        const userToSave = await fetchUserDetails(_user);
        setUser(userToSave);

        queryClient.invalidateQueries({ queryKey: ["user-details", _user.userData.contactID] });
      } catch (error) {
        console.log(error);
      }
    },
    [setUser, queryClient]
  );

  return {
    refreshUserDetails,
  };
};
